import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUsers, FaCalendarAlt, FaTasks, FaComments, FaMoneyBillWave, FaCamera, FaArrowLeft } from 'react-icons/fa';
import {
  TIER_CONFIG,
  CORE_FEATURES,
  PROFESSIONAL_CATEGORIES,
} from '../config/landingConfig';
import { BrandLogo } from '../components/BrandLogo';

// Tier badge component
const TierBadge = ({ tier, size = 'normal' }) => {
  const config = TIER_CONFIG[tier];
  const sizeClasses = size === 'large'
    ? 'px-3 py-1 text-sm font-semibold'
    : 'px-2.5 py-1 text-xs font-bold';
  return (
    <span className={`inline-flex items-center rounded-full border-2 ${config.style} ${sizeClasses}`}>
      {config.label}
    </span>
  );
};

// Tier section header
const TierSectionHeader = ({ tier, description }) => {
  const config = TIER_CONFIG[tier];
  return (
    <div className={`bg-gradient-to-r ${config.bgGradient} rounded-xl p-4 mb-6`}>
      <h4 className="text-lg font-bold text-gray-800">{config.title}</h4>
      {description && <p className="text-sm text-gray-600 mt-1">{description}</p>}
    </div>
  );
};

const IconMap = { FaCalendarAlt, FaTasks, FaCamera, FaUsers, FaMoneyBillWave, FaComments };

const FeatureCard = ({ feature, tier }) => {
  const Icon = IconMap[feature.icon];
  const config = TIER_CONFIG[tier];
  return (
    <div
      className={`bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition border-l-4 ${config.borderColor}`}
      data-testid={`feature-${feature.id}`}
    >
      <div className="flex justify-between items-start mb-3">
        {Icon && <Icon className="text-blue-600 text-3xl" />}
        <TierBadge tier={tier} />
      </div>
      <h4 className="text-lg font-bold text-gray-800 mb-2">{feature.title}</h4>
      <p className="text-gray-600 text-sm">{feature.description}</p>
    </div>
  );
};

const ProfessionalCard = ({ professional }) => (
  <div className={`bg-gradient-to-br ${professional.gradient} p-6 rounded-xl shadow-md hover:shadow-lg transition`}>
    <div className={`w-14 h-14 ${professional.iconBg} rounded-full flex items-center justify-center mb-4`}>
      <span className="text-2xl">{professional.icon}</span>
    </div>
    <h4 className="text-lg font-bold text-gray-800 mb-2">{professional.title}</h4>
    <p className="text-gray-600 text-sm mb-4">{professional.description}</p>
    <ul className="text-sm text-gray-500 space-y-1">
      {professional.services.map((service, idx) => (
        <li key={idx}>• {service}</li>
      ))}
    </ul>
  </div>
);

function Features() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2"
            data-testid="features-home-btn"
          >
            <BrandLogo className="w-9 h-9 sm:w-11 sm:h-11" testId="features-header-logo" />
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Legacy Forge Family</h1>
          </button>
          <div className="flex items-center space-x-2 sm:space-x-4">
            <button
              onClick={() => navigate('/pricing')}
              className="px-3 sm:px-4 py-2 text-gray-600 hover:text-gray-900 font-medium text-sm sm:text-base"
              data-testid="features-pricing-btn"
            >
              Pricing
            </button>
            <button
              onClick={() => navigate('/register')}
              className="px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm sm:text-base"
              data-testid="features-signup-btn"
            >
              Sign Up
            </button>
          </div>
        </div>
      </header>

      {/* Intro */}
      <section className="container mx-auto px-4 py-10 sm:py-14 text-center">
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium mb-6"
          data-testid="features-back-btn"
        >
          <FaArrowLeft /> Back to Home
        </button>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
          All Features, Organized by Tier
        </h2>
        <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
          Every tool in Legacy Forge, grouped by the plan that includes it. Start on Free and grow as your family's needs grow.
        </p>
        <div className="flex flex-wrap justify-center gap-3 mt-8">
          {Object.keys(TIER_CONFIG).map(tier => (
            <TierBadge key={tier} tier={tier} size="large" />
          ))}
        </div>
      </section>

      {/* Core Features */}
      <section className="container mx-auto px-4 py-10">
        <h3 className="text-2xl sm:text-3xl font-bold text-center text-gray-900 mb-8">
          Core Family Tools
        </h3>

        <TierSectionHeader tier="free" description={TIER_CONFIG.free.description} />
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {CORE_FEATURES.free.map(feature => (
            <FeatureCard key={feature.id} feature={feature} tier="free" />
          ))}
        </div>

        <div className="text-center mb-4">
          <TierBadge tier="rooted" size="large" />
          <p className="text-gray-600 text-sm mt-2">{TIER_CONFIG.rooted.description}</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {CORE_FEATURES.rooted.map(feature => (
            <FeatureCard key={feature.id} feature={feature} tier="rooted" />
          ))}
        </div>

        <TierSectionHeader tier="growing" description={TIER_CONFIG.growing.description} />
        <div className="grid md:grid-cols-1 gap-6 mb-6 max-w-md mx-auto">
          {CORE_FEATURES.growing.map(feature => (
            <FeatureCard key={feature.id} feature={feature} tier="growing" />
          ))}
        </div>
      </section>

      {/* Wealth & Legacy Building */}
      <section className="bg-gradient-to-br from-green-50 to-emerald-100 py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Wealth &amp; Legacy Building
            </h3>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
              Empower your family with financial education and resources to build generational wealth.
            </p>
          </div>

          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <TierBadge tier="rooted" size="large" />
              <span className="text-gray-600 font-medium">Foundation for Financial Growth</span>
            </div>
            <div className="grid md:grid-cols-1 gap-6 max-w-lg">
              <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-emerald-500">
                <h4 className="text-lg font-bold text-gray-800 mb-2">Financial Resource Education</h4>
                <p className="text-gray-600 text-sm">
                  Access information on family trusts, insurance, homeownership, and investing to make informed financial decisions.
                </p>
              </div>
            </div>
          </div>

          <div>
            <div className="text-center mb-4">
              <TierBadge tier="flourishing" size="large" />
              <p className="text-gray-600 text-sm mt-2">Premium Wealth Building</p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-amber-500">
                <h4 className="text-lg font-bold text-gray-800 mb-2">Connect with Professionals</h4>
                <p className="text-gray-600 text-sm">
                  Get connected with vetted financial advisors, attorneys, and CPAs who understand your family's needs.
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-amber-500">
                <h4 className="text-lg font-bold text-gray-800 mb-2">Family Consultations</h4>
                <p className="text-gray-600 text-sm">
                  Join invite-only webinars and family-specific consultations to strengthen your financial foundation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted Professionals */}
      <section className="py-16 bg-white" data-testid="features-professionals-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <TierBadge tier="flourishing" size="large" />
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Connect with Trusted Professionals
            </h3>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
              Build your family's financial future with guidance from experts who specialize in generational wealth.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PROFESSIONAL_CATEGORIES.map(professional => (
              <ProfessionalCard key={professional.id} professional={professional} />
            ))}
          </div>
        </div>
      </section>

      {/* Mentorship */}
      <section className="py-16 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Generational Mentorship &amp; Training
            </h3>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
              Bridge the gap between generations and pass down wisdom, skills, and values.
            </p>
          </div>

          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <TierBadge tier="rooted" size="large" />
              <span className="text-gray-600 font-medium">Build Family Rhythms</span>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-emerald-500 max-w-lg">
              <h4 className="text-lg font-bold text-gray-800 mb-2">Rhythms of Connection</h4>
              <p className="text-gray-600 text-sm">
                Monthly interaction opportunities and badges to encourage learning and family interaction.
              </p>
            </div>
          </div>

          <div className="mb-8">
            <div className="text-center mb-4">
              <TierBadge tier="growing" size="large" />
              <p className="text-gray-600 text-sm mt-2">Structured Mentorship Programs</p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-blue-500">
                <h4 className="text-lg font-bold text-gray-800 mb-2">Youth Mentorship</h4>
                <p className="text-gray-600 text-sm">
                  Match elders with youth for guidance, encouragement, and practical advice.
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-blue-500">
                <h4 className="text-lg font-bold text-gray-800 mb-2">Career Planning</h4>
                <p className="text-gray-600 text-sm">
                  Connect youth with mentors for internships, scholarships, and career guidance.
                </p>
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-3 mb-4">
              <TierBadge tier="flourishing" size="large" />
              <span className="text-gray-600 font-medium">Premium Legacy Tools</span>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-amber-500 max-w-lg">
              <h4 className="text-lg font-bold text-gray-800 mb-2">Legacy Storytelling</h4>
              <p className="text-gray-600 text-sm">
                Record audio/video stories from elders and create digital family heritage books.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Heritage & Spiritual */}
      <section className="bg-gradient-to-br from-amber-50 to-orange-100 py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Heritage &amp; Spiritual Legacy
            </h3>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
              Preserve your family's values, faith, and cultural heritage for future generations.
            </p>
          </div>

          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <TierBadge tier="free" size="large" />
              <span className="text-gray-600 font-medium">Start Preserving Your Story</span>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-gray-400 max-w-lg">
              <h4 className="text-lg font-bold text-gray-800 mb-2">Family Timeline</h4>
              <p className="text-gray-600 text-sm">
                Build an interactive timeline with milestones, photos, and stories across generations.
              </p>
            </div>
          </div>

          <div className="mb-8">
            <div className="text-center mb-4">
              <TierBadge tier="rooted" size="large" />
              <p className="text-gray-600 text-sm mt-2">Establish Family Identity</p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-emerald-500">
                <h4 className="text-lg font-bold text-gray-800 mb-2">Prayer &amp; Gratitude</h4>
                <p className="text-gray-600 text-sm">
                  Share prayer requests and praise reports on a dedicated family prayer board.
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-emerald-500">
                <h4 className="text-lg font-bold text-gray-800 mb-2">Mission Statement</h4>
                <p className="text-gray-600 text-sm">
                  Craft your family mission statement to guide your legacy.
                </p>
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-3 mb-4">
              <TierBadge tier="growing" size="large" />
              <span className="text-gray-600 font-medium">Deepen Family Values</span>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-blue-500 max-w-lg">
              <h4 className="text-lg font-bold text-gray-800 mb-2">Values &amp; Affirmations</h4>
              <p className="text-gray-600 text-sm">
                Create custom faith or value-aligned affirmations and devotional reminders for your family to live by.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-blue-600 py-16">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Ready to get started?
          </h3>
          <p className="text-lg text-blue-100 mb-8 max-w-2xl mx-auto">
            Create a free account and invite your family in minutes. Upgrade when you're ready.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => navigate('/register')}
              className="px-8 py-3 bg-white text-blue-600 rounded-lg hover:bg-gray-100 font-semibold shadow-lg"
              data-testid="features-cta-signup-btn"
            >
              Get Started Free
            </button>
            <button
              onClick={() => navigate('/pricing')}
              className="px-8 py-3 border-2 border-white text-white rounded-lg hover:bg-blue-700 font-semibold"
              data-testid="features-cta-pricing-btn"
            >
              View Pricing
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8">
        <div className="container mx-auto px-4 text-center">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <BrandLogo className="w-9 h-9" testId="features-footer-logo" />
            <h3 className="text-xl font-bold">Legacy Forge Family</h3>
          </div>
          <p className="text-gray-400 text-sm">&copy; 2025 Legacy Forge Family. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default Features;
